import { mentionHandleRegexBase } from '../types/index.js';
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const mentionPrefix = '(^|[\\s(>])';
const mentionSuffix = '(?=\\s|$|[.,!?;:])';
/**
 * Turn `@handle` / known `@displayName` into markdown profile links.
 * Display names are matched exactly (longest first) so apostrophes and other
 * non-letter characters still count when that profile is in `mentions`.
 * Bare `@handle` tokens use the allowed handle charset (letters, digits, `_`, `-`).
 */
export const linkProfileMentions = (source, mentions = []) => {
    const aliases = mentions.flatMap(({ profile }) => {
        var _a, _b;
        const handle = (_a = profile.handle) === null || _a === void 0 ? void 0 : _a.trim();
        if (!handle)
            return [];
        const names = new Set([handle]);
        const displayName = (_b = profile.displayName) === null || _b === void 0 ? void 0 : _b.trim();
        if (displayName)
            names.add(displayName);
        return [...names].map((name) => ({ name, handle }));
    });
    aliases.sort((a, b) => b.name.length - a.name.length);
    let text = source;
    for (const { name, handle } of aliases) {
        const pattern = new RegExp(`${mentionPrefix}@(${escapeRegExp(name)})${mentionSuffix}`, 'gi');
        text = text.replace(pattern, (_full, prefix, mentioned) => `${prefix}[@${mentioned}](/@${handle})`);
    }
    return text.replace(new RegExp(mentionHandleRegexBase, 'g'), (_full, prefix, handle) => `${prefix}[@${handle}](/@${handle})`);
};
export const matchMentionHandles = (text) => {
    if (!text)
        return [];
    const handles = new Set();
    for (const match of text.matchAll(new RegExp(mentionHandleRegexBase, 'g'))) {
        const handle = match[2];
        if (handle)
            handles.add(handle);
    }
    return [...handles];
};
/** Readable text for a shortened preview. Drops markdown marks, keeps words. */
export const markdownPlainText = (markdown) => {
    if (!markdown)
        return '';
    return markdown
        .replace(/```[\s\S]*?```/g, ' ')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/[#>*_~`]+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};
export const firstNWords = (markdown, n) => {
    if (!markdown)
        return '';
    // Regular expression to match words and markdown links.
    const regex = /\[.*?\]\(.*?\)|(\w+)/g;
    let wordCount = 0;
    const firstTwelveWords = [];
    let match;
    while ((match = regex.exec(markdown)) !== null) {
        // Check if the match is a link or a regular word.
        if (match[1]) {
            // It's a regular word
            firstTwelveWords.push(match[1]);
            wordCount++;
        }
        else {
            // It's a markdown link - treat it as one word
            firstTwelveWords.push(match[0]); // Add the entire link string
            wordCount++;
        }
        if (wordCount >= n) {
            break;
        }
    }
    return firstTwelveWords.join(' ');
};
/** Average adult reading speed (words per minute). */
const DEFAULT_WORDS_PER_MINUTE = 210;
/**
 * Estimate reading time for a markdown article. Strips markdown syntax
 * before counting words, then divides by the words-per-minute rate.
 * Always returns at least 1 for non-empty content.
 */
export const minutesToRead = (markdown, wordsPerMinute = DEFAULT_WORDS_PER_MINUTE) => {
    if (!markdown)
        return 0;
    const plainText = markdownPlainText(markdown);
    const wordCount = plainText.split(/\s+/).filter(Boolean).length;
    return wordCount === 0
        ? 0
        : Math.max(1, Math.ceil(wordCount / wordsPerMinute));
};
